'use client';

import { useEffect } from 'react';
import { useParams } from 'next/navigation';
import { useSuspenseQuery } from '@apollo/client';

import NotFound from '@/app//[locale]/error';

import {
  ListingGroupQuery,
  LOCATION_ENUM,
  LONG_TERM_STAY_PLACEMENT,
} from '@/query';

import { LocationPosts } from '@/component/Blogs';
import { useListingSearchStore } from '@/store';

import { useLocale } from 'next-intl';
import { Hero } from './Hero';
import { Search } from './Search';
import { Page } from './Page';
import { List } from './List';
import { Placement } from './Placement';
import { getListingLongTermVariables } from '@/utils';

const defaultLocation = {
  id: 'SE',
  name: 'Sweden',
  type: LOCATION_ENUM.Country,
};

export const Destinations = () => {
  const { groupSlug } = useParams();
  const locale = useLocale();

  const { data, error } = useSuspenseQuery(ListingGroupQuery, {
    variables: {
      slug: groupSlug,
      locale,
    },
    errorPolicy: 'all',
  });

  const { setLocation, setGuests, setDate } = useListingSearchStore();

  const slug = data?.showListingGroup?.slug || groupSlug;
  const blog = data?.showListingGroup?.blog?.slug;
  const isLongTerm = (data?.showListingGroup?.purposes || []).includes(
    LONG_TERM_STAY_PLACEMENT
  );
  
  const { params, variables } = getListingLongTermVariables(isLongTerm, slug);

  useEffect(() => {
    if (!data?.showListingGroup) {
      return;
    }
    setLocation(data?.showListingGroup?.location || defaultLocation);
    if (isLongTerm) {
      setGuests({ adults: params.adults });
      setDate({ startDate: params.startDate, endDate: params.endDate });
    }
  }, [data]);

  if (error || !data?.showListingGroup) {
    return <NotFound withHeader={false} />;
  }

  return (
    <>
      {!!data?.showListingGroup && (
        <>
          <Hero
            name={data?.showListingGroup?.name}
            headline={data?.showListingGroup?.headline}
            coverImage={data?.showListingGroup?.coverImage}
          >
            <Search name={data?.showListingGroup?.name} />
          </Hero>
          <Page
            group={data?.showListingGroup}
            name={data?.showListingGroup?.name}
            page={1}
            variables={variables}
          />
        </>
      )}

      {!!blog && (
        <LocationPosts blog={blog} classesWrap="max-3xl:pt-10 max-3xl:pb-16" />
      )}
      <List group={data?.showListingGroup} variables={variables} />
      {!!data?.showListingGroup?.description && (
        <Placement
          name={data?.showListingGroup?.name}
          coverImage={data?.showListingGroup?.coverImage}
          description={data?.showListingGroup?.description}
        />
      )}
    </>
  );
};
